import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@mantine/core';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { INSURANCE_OPTION_CLICKED } from '../../../../../../constants/analytics-events';
import { useInsurancePreference, INSURANCE_PREFERENCE, type InsurancePreference } from '../../../../../../hooks/use-insurance-preference';
import { getAutomationsSettingsPath } from '../../../../../../routes/constants';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { InsureByRulesContent } from './components/insure-by-rules-content/insure-by-rules-content';
import { messages } from './messages';
import styles from './insurance-preference-radio.module.less';

const INSURANCE_SETTINGS_PAGE = 'Insurance Settings';
const INSURANCE_SETTINGS_SOURCE = 'insurance_settings_preferences';

const OPTION_CLICKED_LABELS: Record<string, string> = {
  [INSURANCE_PREFERENCE.INSURE_ALL]: 'All',
  [INSURANCE_PREFERENCE.INSURE_BY_RULES]: 'Rule',
  [INSURANCE_PREFERENCE.NONE]: 'None',
};

type InsurancePreferenceRadioProps = {
  hasActiveInsuranceRule: boolean;
  hasParcelGuardAutomationRule?: boolean;
  hasValidationError: boolean;
  hasNoCoveragePreference?: boolean;
  onPreferenceChange: (preference: InsurancePreference) => void;
};

export const InsurancePreferenceRadio = ({
  hasActiveInsuranceRule,
  hasParcelGuardAutomationRule = false,
  hasValidationError,
  hasNoCoveragePreference = false,
  onPreferenceChange,
}: InsurancePreferenceRadioProps) => {
  const { insurancePreference, isLoading } = useInsurancePreference();
  const [userSelection, setUserSelection] = useState<InsurancePreference | null>(null);

  const selectedOption = useMemo(() => {
    if (userSelection !== null) return userSelection;
    if (hasNoCoveragePreference) return null;
    return insurancePreference;
  }, [userSelection, insurancePreference, hasNoCoveragePreference]);

  const shouldShowNoCoverageBanner = !hasParcelGuardAutomationRule && selectedOption === INSURANCE_PREFERENCE.NONE;

  const trackAndSelect = useCallback(
    (preference: InsurancePreference) => {
      setUserSelection(preference);
      onPreferenceChange(preference);
      Segment.trackEvent(INSURANCE_OPTION_CLICKED, {
        page: INSURANCE_SETTINGS_PAGE,
        selection: preference,
        source: INSURANCE_SETTINGS_SOURCE,
        optionClicked: OPTION_CLICKED_LABELS[preference],
      });
    },
    [onPreferenceChange],
  );

  const handleSelectInsureAll = useCallback(() => {
    trackAndSelect(INSURANCE_PREFERENCE.INSURE_ALL);
  }, [trackAndSelect]);

  const handleSelectInsureByRules = useCallback(() => {
    trackAndSelect(INSURANCE_PREFERENCE.INSURE_BY_RULES);
  }, [trackAndSelect]);

  const handleSelectNone = useCallback(() => {
    trackAndSelect(INSURANCE_PREFERENCE.NONE);
  }, [trackAndSelect]);

  if (isLoading) {
    return (
      <div className={styles['apply-insurance-radio-section']}>
        <Text textType={TextType.Label} value={messages.applyInsuranceLabel} />
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className={styles['apply-insurance-radio-section']}>
      <Text className={styles['apply-insurance-radio-label']} textType={TextType.Label} value={messages.applyInsuranceLabel} />
      {hasParcelGuardAutomationRule && (
        <BannerMessageV2
          className={styles['active-parcel-guard-rules-banner']}
          content={
            <Text
              formattedMessageValues={{
                a: (chunks: React.ReactNode) => <Link to={getAutomationsSettingsPath()}>{chunks}</Link>,
              }}
              textType={TextType.Paragraph}
              value={messages.activeParcelGuardRulesBannerBody}
            />
          }
          header={<Text textType={TextType.Label} value={messages.activeParcelGuardRulesBannerTitle} />}
          layout={BannerLayout.Full}
          status={BannerStatus.Info}
        />
      )}
      <RadioCheckList className={styles['radio-list']}>
        <RadioCheckListItem isChecked={selectedOption === INSURANCE_PREFERENCE.INSURE_ALL} onChecked={handleSelectInsureAll}>
          <FlexRow gap={Gap.Space8}>
            <Text textType={TextType.Paragraph} value={messages.insureAllTitle} />
            <Badge color='inform' size='sm' variant='outline'>
              <Text textType={TextType.Caption} value={messages.recommendedBadge} />
            </Badge>
          </FlexRow>
        </RadioCheckListItem>
        {selectedOption === INSURANCE_PREFERENCE.INSURE_ALL && (
          <Text className={styles['radio-option-description']} textType={TextType.Description} value={messages.insureAllDescription} />
        )}
        {!hasParcelGuardAutomationRule && (
          <>
            <RadioCheckListItem isChecked={selectedOption === INSURANCE_PREFERENCE.INSURE_BY_RULES} onChecked={handleSelectInsureByRules}>
              <Text textType={TextType.Paragraph} value={messages.insureByRulesTitle} />
            </RadioCheckListItem>
            {selectedOption === INSURANCE_PREFERENCE.INSURE_BY_RULES && (
              <InsureByRulesContent hasActiveInsuranceRule={hasActiveInsuranceRule} hasValidationError={hasValidationError} />
            )}
          </>
        )}
        <RadioCheckListItem isChecked={selectedOption === INSURANCE_PREFERENCE.NONE} onChecked={handleSelectNone}>
          <Text textType={TextType.Paragraph} value={messages.insureSkipTitle} />
        </RadioCheckListItem>
      </RadioCheckList>
      {hasParcelGuardAutomationRule && selectedOption === INSURANCE_PREFERENCE.NONE && (
        <BannerMessageV2
          className={styles['skip-with-active-rules-warning']}
          content={<Text textType={TextType.Paragraph} value={messages.skipWithActiveRulesWarning} />}
          layout={BannerLayout.Full}
          status={BannerStatus.Warning}
        />
      )}
      {shouldShowNoCoverageBanner && (
        <BannerMessageV2
          className={styles['skip-with-active-rules-warning']}
          content={<Text textType={TextType.Paragraph} value={messages.noCoverageWarning} />}
          layout={BannerLayout.Full}
          status={BannerStatus.Warning}
        />
      )}
    </div>
  );
};
